<script setup lang="ts">
import LeInput from "@dsk/components/Form/LeInput.vue";
import LeSelect from "@dsk/components/Form/LeSelect.vue";
import LeToggle from "@dsk/components/LeToggle.vue";
import LePaginate from "@dsk/components/Pagination/LePaginate.vue";
import LeSelectorButton from "@dsk/components/SelectorButton/LeSelectorButton.vue";
import LeSelectorButtonGroup from "@dsk/components/SelectorButton/LeSelectorButtonGroup.vue";
import LeMagnifyingGlass from "@dsk/icons/LeMagnifyingGlass.vue";
import { useFuse } from "@vueuse/integrations/useFuse";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import LeSkeleton from "@/components/LeSkeleton.vue";
import { useResponsiveness } from "@/composables";
import { DictionariesHelper } from "@/helpers";
import type { Dictionary, DictionaryListFilter, LeSelectOption } from "@/types";

import LeAboutDictionaryModal from "./LeAboutDictionaryModal.vue";
import LeDictionary from "./LeDictionary.vue";

interface Props {
  dictionaries: Dictionary[];
  filters: DictionaryListFilter[];
  showDescriptionsByDefault?: boolean;
  showDescriptionsToggle?: boolean;
  withAboutModal?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  showDescriptionsByDefault: false,
  showDescriptionsToggle: false
});

const isLoading = computed<boolean>(() => props.dictionaries.length === 0);

let defaultActiveFilterIndex = props.filters.findIndex((filter) => filter.isDefault);
defaultActiveFilterIndex = defaultActiveFilterIndex === -1 ? 0 : defaultActiveFilterIndex;

const { t } = useI18n();
const { isSmallerThanTabletScreen } = useResponsiveness();

const query = ref<string>("");
const isSearching = computed<boolean>(() => query.value.length > 0);
const activeFilterIndex = ref<number>(defaultActiveFilterIndex);
const activeFilter = computed<DictionaryListFilter>(() => props.filters[activeFilterIndex.value]);
const dictionariesInTab = computed<Dictionary[]>(() =>
  DictionariesHelper.sortByPublisherPriority(
    props.dictionaries.filter((dictionary) =>
      dictionary.tags.some(
        (tag) => activeFilter.value.dictionaryTags === null || activeFilter.value.dictionaryTags.includes(tag)
      )
    )
  )
);
const { results: dictionariesInFilter } = useFuse(query, dictionariesInTab, {
  matchAllWhenSearchEmpty: true,
  fuseOptions: {
    keys: ["title", "publisher"],
    threshold: 0.3
  }
});
watch(
  () => isSearching.value,
  (value) => {
    if (!value || activeFilter.value.isSearchable) {
      return;
    }

    const searchableFilterIndex = props.filters.findIndex((filter) => filter.isSearchable);
    if (searchableFilterIndex === -1) {
      return;
    }

    activeFilterIndex.value = searchableFilterIndex;
  }
);
const isFilterDisabled = (filter: DictionaryListFilter): boolean => isSearching.value && !filter.isSearchable;

const dropdownOptions = computed<LeSelectOption<number>[]>(() =>
  props.filters.map((filter, index) => ({
    text: filter.title,
    value: index,
    disabled: isFilterDisabled(filter)
  }))
);

const showDescriptions = ref<boolean>(props.showDescriptionsByDefault);

const pagination = ref<typeof LePaginate>();
const currentPage = ref<number>(0);
const dictionariesPerPage = computed<number>(() => {
  if (isSmallerThanTabletScreen.value) {
    return 6;
  }

  return showDescriptions.value ? 9 : 18;
});
const totalPages = computed<number>(() =>
  Math.max(1, Math.ceil(dictionariesInFilter.value.length / dictionariesPerPage.value))
);
const hasResults = computed<boolean>(() => dictionariesInFilter.value.length > 0);

watch(totalPages, () => {
  currentPage.value = 0;
});

const dictionariesOnPage = computed<Dictionary[]>(() => {
  const start = currentPage.value * dictionariesPerPage.value;
  const end = start + dictionariesPerPage.value;
  return dictionariesInFilter.value.map((m) => m.item).slice(start, end);
});

const selectedDictionary = ref<Dictionary | null>(null);
const openAboutDictionaryModal = (dictionary: Dictionary) => {
  if (!props.withAboutModal) {
    return;
  }

  selectedDictionary.value = dictionary;
};
</script>

<template>
  <div class="le-dictionary-list">
    <section class="le-dictionary-list__filter">
      <div class="le-dictionary-list__filter-dropdown">
        <LeSelect v-model="activeFilterIndex" :options="dropdownOptions" width="100%" />
      </div>

      <LeSelectorButtonGroup
        class="le-dictionary-list__filter-buttons"
        :items="props.filters"
        :selected="activeFilterIndex"
        @input="activeFilterIndex = $event.index"
      >
        <template #default="{ state, handleBlur, handleFocusNext, handleFocusPrev, handleSelect }">
          <LeSelectorButton
            v-for="(filter, index) in props.filters"
            :key="filter.title"
            class="le-dictionary-list__filter-button"
            :index="index"
            :tabbable="state.tabbableIndex === index"
            :selected="state.selectedIndex === index"
            :disabled="isFilterDisabled(filter)"
            @blur="handleBlur()"
            @select="handleSelect($event)"
            @focus-next="handleFocusNext($event)"
            @focus-prev="handleFocusPrev($event)"
          >
            {{ filter.title }}
          </LeSelectorButton>
        </template>
      </LeSelectorButtonGroup>

      <div class="le-dictionary-list__filter-search-container">
        <LeInput v-model="query" :placeholder="t('le-dictionary-list.search-placeholder')">
          <template #iconAfter>
            <LeMagnifyingGlass role="presentation" />
          </template>
        </LeInput>
      </div>
    </section>
    <section
      id="le-dictionary-list__dictionaries"
      class="le-dictionary-list__dictionaries"
      :class="{
        'le-dictionary-list__dictionaries--with-descriptions': showDescriptions,
        'le-dictionary-list__dictionaries--no-results': !isLoading && !hasResults,
        'le-dictionary-list__dictionaries--multiple-pages': totalPages > 1,
        'le-dictionary-list__dictionaries--is-loading': isLoading
      }"
      :aria-describedby="pagination?.statusId"
    >
      <template v-if="isLoading">
        <LeSkeleton v-for="index in dictionariesPerPage" :key="index" width="100%" height="100%" />
      </template>
      <template v-else>
        <template v-if="hasResults">
          <LeDictionary
            v-for="dictionary in dictionariesOnPage"
            :key="dictionary.id"
            :dictionary="dictionary"
            :show-icon="props.withAboutModal"
            :show-description="showDescriptions"
            :tabindex="props.withAboutModal ? 0 : -1"
            class="le-dictionary-list__dictionary"
            :class="{ 'le-dictionary-list__dictionary--clickable': props.withAboutModal }"
            @click="openAboutDictionaryModal(dictionary)"
            @keyup.enter="openAboutDictionaryModal(dictionary)"
            @keydown.space.stop="openAboutDictionaryModal(dictionary)"
          />
        </template>
        <p v-else class="le-dictionary-list__no-results-text">
          <i18n-t keypath="le-dictionary-list.no-results">
            <template #query>
              <strong>{{ query }}</strong>
            </template>
          </i18n-t>
        </p>
      </template>
    </section>
    <section class="le-dictionary-list__bottom">
      <LePaginate
        ref="pagination"
        v-model:current-page="currentPage"
        class="le-dictionary-list__pagination"
        controls="le-dictionary-list__dictionaries"
        :label="t('le-dictionary-list.pagination')"
        :total-pages="totalPages"
      />
      <span v-if="props.showDescriptionsToggle" class="le-dictionary-list__descriptions-toggle">
        {{ t("le-dictionary-list.show-descriptions") }}
        <LeToggle v-model="showDescriptions" />
      </span>
    </section>

    <LeAboutDictionaryModal
      v-if="selectedDictionary"
      :is-open="selectedDictionary !== null"
      :dictionary="selectedDictionary"
      @close="selectedDictionary = null"
    />
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all.less";

.le-dictionary-list {
  &__filter-dropdown {
    #media-breakpoint-up(md, {
      display: none;
    });
  }

  &__filter {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: 12px;

    #media-breakpoint-up(md, {
      flex-direction: row;
      justify-content: space-between;

      &-dropdown {
        display: none;
      }
    });

    &-buttons {
      display: none;

      #media-breakpoint-up(md, {
        display: flex;
        gap: 6px;
      });
    }

    &-button {
      padding: 10px 16px;
    }

    &-search-container {
      width: 100%;
      height: 40px;

      #media-breakpoint-up(md, {
        width: 263px;
      });
    }
  }

  &__dictionaries {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 24px;
    margin-bottom: 24px;

    #media-breakpoint-up(md, {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      grid-template-rows: repeat(6, 1fr);
      gap: 16px;
      min-height: 488px; // This will ensure the height does not jump when descriptions are <=3 lines
    });

    // UX: Ensures list height doesn't jump on pages with <6 dictionaries when there a multiple pages
    //     For single-page UX is improved by not having empty space between filter / pagination.
    &--multiple-pages {
      display: grid;
      grid-template-rows: repeat(6, 1fr);
      min-height: 468px;

      #media-breakpoint-up(md, {
        min-height: 488px; // This will ensure the height does not jump when descriptions are <=3 lines
      });
    }

    &--with-descriptions {
      #media-breakpoint-up(md, {
        grid-template-rows: repeat(3, 1fr);
      });
    }

    &--no-results {
      grid-template-columns: 1fr;
      justify-items: center;
      align-items: flex-start;
    }

    &--is-loading {
      display: grid;
      grid-template-rows: repeat(6, 1fr);
      min-height: 825px;

      #media-breakpoint-up(md, {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(3, 1fr);
        min-height: 488px; // This will ensure the height does not jump when descriptions are <=3 lines
      });
    }
  }

  &__dictionary {
    &--clickable {
      cursor: pointer;

      &:hover {
        box-shadow: @shadows[floating-hover];
      }

      &.focus-visible {
        .focus-outline(yellow);
      }
    }
  }

  &__no-results-text {
    margin-bottom: 0;
    .body-text("m");
    color: @colors[@neutrals][500];
    text-align: center;
    word-break: break-all;

    #media-breakpoint-up(md, {
      margin-top: 36px;
    });
  }

  &__bottom {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    margin: 26px -26px 0;

    #media-breakpoint-up(sm, {
      margin: 26px 0 0 0;
    });
  }

  &__pagination {
    grid-column: 2;
    justify-self: center;
  }

  &__descriptions-toggle {
    display: none;

    #media-breakpoint-up(md, {
      display: flex;
      grid-column: 3;
      justify-self: end;
      gap: 12px;
    });
  }
}
</style>
